/**
 * @file components/Navbar.js
 * @description Barre de navigation principale
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <style>{`
        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          padding: 0 0;
          transition: all 0.3s ease;
        }
        .navbar.scrolled {
          background: rgba(20, 29, 48, 0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(169, 134, 103, 0.2);
          box-shadow: 0 4px 24px rgba(0,0,0,0.3);
        }
        .navbar-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .navbar-logo {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.6rem;
          font-weight: 700;
          color: #FFFFFF;
          display: flex;
          align-items: center;
          gap: 10px;
          letter-spacing: 0.5px;
        }
        .navbar-logo span {
          color: #A98667;
        }
        .logo-icon {
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, #A98667, #c4a07d);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
        }
        .navbar-links {
          display: flex;
          align-items: center;
          gap: 8px;
          list-style: none;
        }
        .navbar-links a, .nav-btn-ghost {
          font-size: 0.9rem;
          font-weight: 500;
          color: rgba(240, 234, 226, 0.8);
          padding: 8px 16px;
          border-radius: 8px;
          transition: all 0.2s ease;
          background: none;
          border: none;
          cursor: pointer;
          letter-spacing: 0.3px;
        }
        .navbar-links a:hover, .nav-btn-ghost:hover {
          color: #FFFFFF;
          background: rgba(255,255,255,0.08);
        }
        .navbar-links a.active {
          color: #A98667;
          background: rgba(169, 134, 103, 0.12);
        }
        .nav-btn-primary {
          background: #A98667;
          color: #FFFFFF !important;
          padding: 9px 20px;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
          transition: all 0.2s ease;
          border: 2px solid #A98667;
        }
        .nav-btn-primary:hover {
          background: #c4a07d !important;
          border-color: #c4a07d !important;
          transform: translateY(-1px);
        }
        .nav-user {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .nav-user-name {
          font-size: 0.85rem;
          color: rgba(240, 234, 226, 0.7);
        }
        .nav-user-name strong {
          color: #A98667;
        }
        /* Mobile */
        .hamburger {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
        }
        .hamburger span {
          width: 24px;
          height: 2px;
          background: #FFFFFF;
          border-radius: 2px;
          transition: all 0.3s ease;
        }
        .mobile-menu {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #141d30;
          z-index: 999;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 24px;
        }
        .mobile-menu.open {
          display: flex;
        }
        .mobile-menu a, .mobile-menu button {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2rem;
          font-weight: 600;
          color: #FFFFFF;
          background: none;
          border: none;
          cursor: pointer;
          transition: color 0.2s;
        }
        .mobile-menu a:hover, .mobile-menu button:hover {
          color: #A98667;
        }
        .mobile-close {
          position: absolute;
          top: 24px;
          right: 24px;
          font-size: 2rem;
          color: #FFFFFF;
          background: none;
          border: none;
          cursor: pointer;
        }
        @media (max-width: 768px) {
          .hamburger { display: flex; }
          .navbar-links { display: none; }
          .nav-user { display: none; }
        }
      `}</style>

      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-inner">
          <Link to="/" className="navbar-logo">
            <div className="logo-icon">✦</div>
            Verbe<span>Vivant</span>
          </Link>

          <ul className="navbar-links">
            <li><Link to="/" className={isActive('/') ? 'active' : ''}>Accueil</Link></li>
            <li><Link to="/moments" className={isActive('/moments') ? 'active' : ''}>Moments</Link></li>
            <li><Link to="/quiz" className={isActive('/quiz') ? 'active' : ''}>Quiz</Link></li>
            <li><Link to="/chat" className={isActive('/chat') ? 'active' : ''}>Chat</Link></li>
            <li><Link to="/meditation" className={isActive('/meditation') ? 'active' : ''}>Méditation</Link></li>
            <li><Link to="/contact" className={isActive('/contact') ? 'active' : ''}>Contact</Link></li>
            {isAuthenticated ? (
              <li className="nav-user">
                <span className="nav-user-name">Bonjour, <strong>{user?.name}</strong></span>
                <button className="nav-btn-ghost" onClick={handleLogout}>Déconnexion</button>
              </li>
            ) : (
              <>
                <li><Link to="/login" className={isActive('/login') ? 'active' : ''}>Connexion</Link></li>
                <li><Link to="/register" className="nav-btn-primary">S'inscrire</Link></li>
              </>
            )}
          </ul>

          <button className="hamburger" onClick={() => setMenuOpen(true)} aria-label="Menu">
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* Menu mobile */}
      <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
        <button className="mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
        <Link to="/">Accueil</Link>
        <Link to="/moments">Moments</Link>
        <Link to="/quiz">Quiz</Link>
        <Link to="/chat">Chat</Link>
        <Link to="/meditation">Méditation</Link>
        <Link to="/contact">Contact</Link>
        {isAuthenticated ? (
          <button onClick={handleLogout}>Déconnexion</button>
        ) : (
          <>
            <Link to="/login">Connexion</Link>
            <Link to="/register" style={{ color: '#A98667' }}>S'inscrire</Link>
          </>
        )}
      </div>
    </>
  );
};

export default Navbar;